import React, { type CSSProperties } from 'react';
import {
  ArrowBackIcon,
  CloseCrossIcon,
  BuildingOfficeIcon,
  UsersGroupIcon,
  ShareNodesIcon,
  BarChartToolIcon,
  ClipboardTasksIcon,
  PollVoteIcon,
  FolderArchiveDocIcon,
  CalendarScheduleIcon,
} from './DepartmentIcons';
import { DepartmentOverviewView } from './DepartmentOverviewView';
import { DepartmentMonitoringView } from './DepartmentMonitoringView';
import { DepartmentStaffView } from './DepartmentStaffView';
import { DepartmentTasksView } from './DepartmentTasksView';
import { DepartmentSurveysView } from './DepartmentSurveysView';
import { DepartmentDocumentsView } from './DepartmentDocumentsView';
import { DepartmentAnalyticsView } from './DepartmentAnalyticsView';
import { DepartmentEventsView } from './DepartmentEventsView';

interface DepartmentWorkspaceOverlayProps {
  currentView: string;
  onChangeView: (view: string) => void;
  onClose: () => void;
  roleTitle: string;
  summary: any;
  onOpenSuperAi: () => void;
}

/** Department workspace — cab-overlay chrome + cabinet-* nav. */
export const DepartmentWorkspaceOverlay: React.FC<DepartmentWorkspaceOverlayProps> = ({
  currentView,
  onChangeView,
  onClose,
  roleTitle,
  summary,
  onOpenSuperAi,
}) => {
  const navItems = [
    {
      id: 'overview',
      title: 'Панель кафедры',
      subtitle: 'Сводка, контроль и быстрые действия',
      icon: <BuildingOfficeIcon className="w-5 h-5" />,
    },
    {
      id: 'monitoring',
      title: 'Мониторинг докторантов',
      subtitle: 'Реестр обучения и защит внутри панели',
      icon: <UsersGroupIcon className="w-5 h-5" />,
    },
    {
      id: 'staff',
      title: 'Сотрудники и нагрузка',
      subtitle: 'Состав, роли, нагрузка и ответственные',
      icon: <ShareNodesIcon className="w-5 h-5" />,
    },
    {
      id: 'tasks',
      title: 'Поручения',
      subtitle: 'Задачи, получатели, сроки и исполнение',
      icon: <ClipboardTasksIcon className="w-5 h-5" />,
    },
    {
      id: 'surveys',
      title: 'Опросы и запросы',
      subtitle: 'Быстрый сбор актуальных сведений',
      icon: <PollVoteIcon className="w-5 h-5" />,
    },
    {
      id: 'documents',
      title: 'Документы кафедры',
      subtitle: 'Файлы, версии и комплектность',
      icon: <FolderArchiveDocIcon className="w-5 h-5" />,
    },
    {
      id: 'analytics',
      title: 'Отчётность и аналитика',
      subtitle: 'Сводки, риски и экспорт',
      icon: <BarChartToolIcon className="w-5 h-5" />,
    },
    {
      id: 'events',
      title: 'Встречи и дежурства',
      subtitle: 'События, приглашения и подтверждения',
      icon: <CalendarScheduleIcon className="w-5 h-5" />,
    },
  ];

  const handleBack = () => {
    if (currentView !== 'overview') {
      onChangeView('overview');
    } else {
      onClose();
    }
  };

  return (
    <div id="d126Overlay" className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Кафедральные операции академии</small>
              <h2>Кафедральный рабочий центр</h2>
              <p>
                Панель кафедры · мониторинг докторантов · сотрудники · документы · аналитика ·
                встречи
              </p>
            </div>
          </div>
          <div className="cab-top-actions">
            <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
              {roleTitle}
            </span>
            <button type="button" className="cab-btn" onClick={handleBack}>
              <ArrowBackIcon className="w-3.5 h-3.5" />
              Назад
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseCrossIcon className="w-4 h-4" />
            </button>
            <button type="button" className="cab-btn" onClick={onClose}>
              Закрыть
            </button>
          </div>
        </div>

        <div
          className="cab-body"
          style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: 14 }}
        >
          <aside className="cabinet-panel">
            <header>
              <div>
                <div className="cabinet-eyebrow">Казахское языкознание</div>
                <h3>{roleTitle}</h3>
                <p>
                  Общий кафедральный контур. Состав и служебные данные синхронизируются между
                  разрешёнными кабинетами.
                </p>
              </div>
            </header>
            <div className="cabinet-panel-body">
              <div className="cabinet-filters" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                {navItems.map((item) => {
                  const isActive = currentView === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`cabinet-filter${isActive ? ' active' : ''}`}
                      style={{
                        justifyContent: 'flex-start',
                        display: 'flex',
                        gap: 10,
                        textAlign: 'left',
                        borderRadius: 8,
                        height: 'auto',
                        padding: '10px 12px',
                      }}
                      onClick={() => onChangeView(item.id)}
                    >
                      <span aria-hidden="true">{item.icon}</span>
                      <span>
                        <strong style={{ display: 'block', fontSize: 12 }}>{item.title}</strong>
                        <span style={{ display: 'block', fontSize: 10, opacity: 0.75 }}>
                          {item.subtitle}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <main className="cabinet-stack">
            {currentView === 'overview' && (
              <DepartmentOverviewView
                summary={summary}
                onNavigateView={onChangeView}
                onOpenSuperAi={onOpenSuperAi}
              />
            )}
            {currentView === 'monitoring' && <DepartmentMonitoringView />}
            {currentView === 'staff' && <DepartmentStaffView />}
            {currentView === 'tasks' && <DepartmentTasksView />}
            {currentView === 'surveys' && <DepartmentSurveysView />}
            {currentView === 'documents' && <DepartmentDocumentsView />}
            {currentView === 'analytics' && <DepartmentAnalyticsView />}
            {currentView === 'events' && <DepartmentEventsView />}
          </main>
        </div>
      </div>
    </div>
  );
};
